import React, { useEffect, useRef } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import Button from '@material-ui/core/Button';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import PauseIcon from '@material-ui/icons/Pause';
import { Box, Divider, Paper, Typography, useTheme } from '@material-ui/core';
import config from '../../../../config';
import { useOrganizationId } from '../../../../contexts/OrganizationIdContext';
import { useChartingSession } from './Charting';
import { gql, useMutation, useSubscription } from '@apollo/client';
import { tryParseJson } from '../../../../util/parseJson';
import { useAccessToken } from '../AccessToken';
import { RefreshRounded } from '@material-ui/icons';
import withStyles from '@material-ui/core/styles/withStyles';

interface DictationPanelProps {
  patientId: number;
}

const DictationPanel = ({ patientId }: DictationPanelProps) => {
  const theme = useTheme();
  const organizationId = useOrganizationId();
  const accessToken = useAccessToken();
  const chartingSession = useChartingSession();

  const recorderControls = useAudioRecorder(
    {
      noiseSuppression: true,
      echoCancellation: true,
    },
    e => {
      console.error('onNotAllowedOrFound', e);
    }
  );

  const submitRef = useRef<() => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    if (organizationId && accessToken && chartingSession && recorderControls.recordingBlob) {
      void (async () => {
        const searchParams = new URLSearchParams();
        searchParams.append('hospital', organizationId);
        searchParams.append('token', accessToken);
        searchParams.append('patientId', patientId.toString());
        searchParams.append('pin', chartingSession.pin);

        const response = await fetch(`${config.apiURL}/physician/dictation?${searchParams.toString()}`, {
          method: 'POST',
          body: recorderControls.recordingBlob,
        });

        console.log(response);

        const json = await response.json();

        console.log(json);
      })();
    }
  }, [organizationId, accessToken, patientId, chartingSession?.pin, recorderControls.recordingBlob]);

  const dictations = useSubscription(
    gql`
      subscription dictations($patientId: Long!, $pin: String!) {
        dictations(patientId: $patientId, pin: $pin) {
          id
          transcription
        }
      }
    `,
    {
      variables: {
        patientId,
        pin: chartingSession!.pin,
      },
    }
  );

  const start = () => {
    recorderControls.startRecording();
  };

  const stop = () => {
    recorderControls.stopRecording();

    requestIdleCallback(submitRef.current);
  };

  const pause = () => {
    recorderControls.togglePauseResume();
  };

  const resume = () => {
    recorderControls.togglePauseResume();
  };

  const [retryTranscription] = useMutation(
    gql`
      mutation retryTranscription($dictationId: Long!) {
        retryTranscription(dictationId: $dictationId)
      }
    `
  );

  const retry = (dictationId: number) => () => {
    retryTranscription({
      variables: { dictationId },
    });
  };

  const RecordButton = withStyles(theme => ({
    root: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
      },
    },
  }))(Button);

  return (
    <Paper variant="outlined">
      <Box px={4} py={4}>
        <Box>
          <Typography variant="h3" gutterBottom>
            Dictation
          </Typography>
        </Box>

        <Box py={2} style={{ backgroundColor: theme.palette.common.white, position: 'sticky', top: 0 }}>
          {recorderControls.isRecording ? (
            <Box display="flex" alignItems="baseline" style={{ gap: theme.spacing(2) }}>
              {recorderControls.isPaused ? (
                <Box display="flex" style={{ gap: theme.spacing(1) }}>
                  <Box color={theme.palette.error.main}>
                    <RecordButton variant="contained" aria-label="resume" onClick={resume}>
                      Resume
                    </RecordButton>
                  </Box>
                  <Button variant="outlined" color="primary" aria-label="done" onClick={stop}>
                    Done
                  </Button>
                </Box>
              ) : (
                <RecordButton variant="contained" startIcon={<PauseIcon />} aria-label="pause" onClick={pause}>
                  Pause recording
                </RecordButton>
              )}
            </Box>
          ) : (
            <RecordButton variant="contained" startIcon={<FiberManualRecordIcon />} aria-label="delete" onClick={start}>
              Start recording
            </RecordButton>
          )}
        </Box>

        {(dictations.data?.dictations || []).map(d => (
          <Box my={2}>
            <Box mb={2} display="flex" justifyContent="space-between">
              <Box>
                <Typography variant="caption" gutterBottom>
                  Dictation #{d.id}
                </Typography>
                <Typography>{d.transcription}</Typography>
              </Box>
            </Box>
            <Divider />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default DictationPanel;

const formatDurationInSeconds = (duration: number) => {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  return `${padLeft(minutes.toString(10), 2, '0')}:${padLeft(seconds.toString(10), 2, '0')}`;
};

const padLeft = (value: string, length: number, char: string) => value.padStart(length, char);
